import { Link } from "react-router-dom";
import { Seo, localBusinessLD } from "../lib/seo";
import { SITE } from "../data/site";
import { CITIES } from "../data/cities";
import { Reveal, SectionHead } from "../components/Reveal";
import { PageHero, CtaBand } from "../components/Shared";

const PILLARS = [
    {
        num: "01",
        title: "Google Business Profile",
        text: "Notre fiche Google (12 Rue Brachedal, Chaumont-en-Vexin) est notre vitrine numéro un : photos de chantiers à jour, horaires, zones desservies et publications régulières. C'est là que la plupart de nos nouveaux clients nous découvrent.",
        link: { href: "https://www.google.com/search?q=Solino+Pro+12+Rue+Brachedal+Chaumont-en-Vexin", label: "Voir notre fiche Google", external: true },
        testid: "marketing-gbp",
    },
    {
        num: "02",
        title: `Instagram — @solinopro`,
        text: "Chantiers en cours, avant/après, coulisses de l'atelier : notre Instagram montre le travail tel qu'il est, sans mise en scène. Suivez nos réalisations au fil des semaines.",
        link: { href: SITE.instagram, label: "Suivre @solinopro", external: true },
        testid: "marketing-instagram",
    },
    {
        num: "03",
        title: "Zone d'intervention — 40 km",
        text: "Basés à Chaumont-en-Vexin, nous couvrons volontairement un rayon de 40 km : Gisors, Méru, Marines, Magny-en-Vexin, Chars, Beauvais, Pontoise et tout le Vexin français. Une zone resserrée pour des visites rapides et un suivi sérieux.",
        link: { href: "/zones", label: "Voir la carte des zones", external: false },
        testid: "marketing-zones",
    },
    {
        num: "04",
        title: "Avis clients Google",
        text: "Chaque client satisfait est invité à partager son expérience sur Google — le lien d'invitation figure sur nos devis et nos relances. Vos avis nous font progresser et rassurent les futurs clients.",
        link: { href: "/avis", label: "Lire les avis clients", external: false },
        testid: "marketing-avis",
    },
];

export default function PlanMarketingPage() {
    return (
        <>
            <Seo
                title="Plan marketing — La stratégie de visibilité locale de Solino Pro"
                description="Comment Solino Pro se fait connaître à Chaumont-en-Vexin et dans un rayon de 40 km : fiche Google Business Profile, Instagram @solinopro, zone d'intervention 40 km et avis clients Google."
                path="/plan-marketing"
                jsonld={localBusinessLD()}
            />
            <PageHero
                img="https://images.unsplash.com/photo-1597665863042-47e00964d899?q=80&w=1800&auto=format&fit=crop"
                crumb="Transparence — Solino Pro"
                title="Notre plan marketing,"
                serif="dit simplement."
            />

            <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 lg:py-28" data-testid="marketing-intro">
                <SectionHead
                    eyebrow="Visibilité locale"
                    title="Un artisan qui se trouve"
                    serif="là où on le cherche."
                    text="Pas d'agence publicitaire, pas de promesses en l'air : notre présence en ligne repose sur quatre piliers simples, tenus chaque semaine. Vous trouverez ici exactement comment Solino Pro travaille sa visibilité à Chaumont-en-Vexin et autour."
                />
            </section>

            <section className="border-y border-linew bg-coal/60" data-testid="marketing-pillars">
                <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 lg:py-28 grid md:grid-cols-2 gap-4">
                    {PILLARS.map((p, i) => (
                        <Reveal key={p.num} delay={i * 0.07}>
                            <article className="h-full border border-linew bg-ink p-8 lg:p-10 flex flex-col">
                                <span className="font-display italic text-5xl text-gold/25" aria-hidden="true">{p.num}</span>
                                <h2 className="mt-4 text-xl lg:text-2xl text-ivory font-medium">{p.title}</h2>
                                <p className="mt-4 flex-1 text-sm text-smoke leading-relaxed">{p.text}</p>
                                {p.link.external ? (
                                    <a
                                        href={p.link.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-7 inline-flex items-center gap-2 text-gold text-sm font-semibold uppercase tracking-[0.15em] hover:gap-4 transition-all duration-300"
                                        data-testid={`${p.testid}-link`}
                                    >
                                        {p.link.label} →
                                    </a>
                                ) : (
                                    <Link
                                        to={p.link.href}
                                        className="mt-7 inline-flex items-center gap-2 text-gold text-sm font-semibold uppercase tracking-[0.15em] hover:gap-4 transition-all duration-300"
                                        data-testid={`${p.testid}-link`}
                                    >
                                        {p.link.label} →
                                    </Link>
                                )}
                            </article>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 lg:py-28" data-testid="marketing-coverage">
                <SectionHead
                    eyebrow="Nous couvrons"
                    title="Huit villes principales,"
                    serif="tout le Vexin pour horizon."
                />
                <div className="mt-10 flex flex-wrap gap-3">
                    {CITIES.map((c) => (
                        <Link
                            key={c.slug}
                            to={`/peintre-${c.slug}`}
                            className="rounded-full border border-linew px-5 py-2.5 text-sm text-ivory/75 hover:border-gold hover:text-gold transition-colors"
                            data-testid={`marketing-city-${c.slug}`}
                        >
                            {c.name}
                        </Link>
                    ))}
                </div>
                <Reveal delay={0.15}>
                    <div className="mt-12 border border-linew overflow-hidden" data-testid="marketing-map">
                        <iframe
                            title="Zone d'intervention Solino Pro — Chaumont-en-Vexin"
                            src="https://www.google.com/maps?q=Solino%20Pro%2C%2012%20Rue%20Brachedal%2C%2060240%20Chaumont-en-Vexin%2C%20France&output=embed"
                            width="100%"
                            height="300"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            allowFullScreen
                            style={{ border: 0, filter: "grayscale(0.35)" }}
                        />
                    </div>
                </Reveal>
            </section>

            <CtaBand />
        </>
    );
}
